'use client';

import { useMemo } from 'react';
import { useAccountsContext } from '../contexts/AccountsContext';
import { useCustomerDataContext } from '../contexts/CustomerDataContext';
import {
    RESTRICTION_CODES,
    RestrictionCode,
    isRestrictionCode,
} from '../accountSettings/invitations/restrictionTypes';

export type SecondaryAccountRestrictions = {
    isSecondaryAccount: boolean;
    activeRestrictions: RestrictionCode[];
    hasRestriction: (code: string) => boolean;
    canViewMessages: boolean;
    canViewAnnouncements: boolean;
    canViewFinancials: boolean;
    canViewAuctionPrices: boolean;
    canEditProfile: boolean;
    canViewContainerInfo: boolean;
    canViewShippingRates: boolean;
    canViewTowingCharges: boolean;
    canViewExportArchive: boolean;
    canFillFppiForm: boolean;
    canViewNraDocument: boolean;
    canViewCopartTitleCheck: boolean;
    canMarkAnnouncementsAsRead: boolean;
    canViewAuctionInvoices: boolean;
    canViewPurchaseInvoice: boolean;
    canAccessFullAccountSettings: boolean;
    canEditVehicleInfo: boolean;
    canViewLoadingList: boolean;
    canEditLoadingList: boolean;
    canViewAccessLimits: boolean;
};

export const useSecondaryAccountRestrictions = (): SecondaryAccountRestrictions => {
    const { isPrimaryAccount, selectedAccount } = useAccountsContext();
    const { customerData } = useCustomerDataContext();
    const isSecondaryAccount = selectedAccount !== null && !isPrimaryAccount;

    const activeRestrictions = useMemo(() => {
        if (!isSecondaryAccount) {
            return [] as RestrictionCode[];
        }

        return (
            // Chained over a short list; merging the passes would buy no measurable win.
            // react-doctor-disable-next-line react-doctor/js-combine-iterations
            customerData?.restrictions
                ?.filter(item => item.isRestricted)
                .map(item => item.restriction)
                .filter(isRestrictionCode) ?? []
        );
    }, [customerData?.restrictions, isSecondaryAccount]);

    const hasRestriction = (code: string) => activeRestrictions.includes(code as RestrictionCode);

    const isCommunicationRestricted = hasRestriction(RESTRICTION_CODES.COMMUNICATION);
    const isLoadingListViewRestricted = hasRestriction(RESTRICTION_CODES.LOADING_LIST_VIEW);
    const isLoadingListEditRestricted =
        isLoadingListViewRestricted || hasRestriction(RESTRICTION_CODES.LOADING_LIST_EDIT);

    return {
        isSecondaryAccount,
        activeRestrictions,
        hasRestriction,
        canViewMessages: !isSecondaryAccount || !isCommunicationRestricted,
        canViewAnnouncements: !isSecondaryAccount || !isCommunicationRestricted,
        canViewFinancials: !isSecondaryAccount,
        canViewAuctionPrices: !isSecondaryAccount,
        canEditProfile: !isSecondaryAccount,
        canViewContainerInfo: true,
        canViewShippingRates: !isSecondaryAccount,
        canViewTowingCharges: !isSecondaryAccount,
        canViewExportArchive: true,
        canFillFppiForm: !isSecondaryAccount,
        canViewNraDocument: !isSecondaryAccount,
        canViewCopartTitleCheck: !isSecondaryAccount,
        canMarkAnnouncementsAsRead: !isSecondaryAccount,
        canViewAuctionInvoices: !isSecondaryAccount,
        canViewPurchaseInvoice: !isSecondaryAccount,
        canAccessFullAccountSettings: !isSecondaryAccount,
        canEditVehicleInfo: !isSecondaryAccount || !hasRestriction(RESTRICTION_CODES.VEHICLES_EXTENDED_EDIT),
        canViewLoadingList: !isSecondaryAccount || !isLoadingListViewRestricted,
        canEditLoadingList: !isSecondaryAccount || !isLoadingListEditRestricted,
        canViewAccessLimits: isSecondaryAccount,
    };
};
